import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Text } from '../../common';
import { FormGroup } from '../../components/form';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import { useSearchParams } from 'react-router-dom';
import mixPanel from '../../utils/helpers/mixPanel';
import { validateWebLinkUrl } from '../../utils/helpers/urlValidator';
import InvalidProjectScreen from './InvalidProjectScreen';
import ProjectClosedScreen from './ProjectClosedScreen';
import ErrorScreen1 from '../../assets/images/ErrorScreen1.png';

const SignUpPage = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding-top: calc(24px + env(safe-area-inset-top, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  padding-left: calc(20px + env(safe-area-inset-left, 0px));
  overflow: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;

  @media (max-height: 740px) {
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
`;

const SignUpContent = styled.div`
  width: min(390px, 100%);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 168px;
  height: auto;
  display: block;
  margin: 12px auto 36px;

  @media (max-height: 740px) {
    width: 132px;
    margin: 4px auto 16px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 12px;
  font-size: 26px;
  line-height: 1.3;
  font-weight: 700;
  color: #333333;
  text-align: center;
`;

const HeroSubtitle = styled.p`
  margin: 0 0 32px;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 400;
  color: #777777;
  text-align: center;

  @media (max-height: 740px) {
    margin-bottom: 16px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #333333;
  text-align: left;
`;

const VerifiedMobileBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f3f3f3;
  box-sizing: border-box;
`;

const MobileValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  word-break: break-all;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #9ebe21;
`;

const CheckIcon = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #9ebe21;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-0.5px, -0.5px);
  }
`;

const NameInput = styled.input`
  width: 100%;
  min-height: 52px;
  padding: 14px;
  border: 1px solid #d8d8d8;
  border-radius: 10px;
  background: #ffffff;
  /* iOS Safari zooms focused inputs under 16px and often stays zoomed after blur */
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #b0b0b0;
    font-weight: 400;
  }

  &:focus {
    border-color: #b7b7b7;
  }
`;

const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 22px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #555555;
  text-align: left;
`;

const TermsCheckbox = styled.input`
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  cursor: pointer;
`;

const TermsLink = styled.a`
  color: #6b7a2e;
  font-weight: 600;
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`;

const CreateAccountButton = styled.button`
  display: block;
  width: 100%;
  flex-shrink: 0;
  margin: 22px 0 0;
  padding: 15px 16px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #2f2f2f;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${props => (props.disabled ? '#d9d9d9' : '#9ebe21')};
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.94;
  }
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 16px;
`;

const DifferentNumberLink = styled.button`
  display: block;
  flex-shrink: 0;
  margin: 18px auto 0;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #6b7a2e;
  text-decoration: underline;
  cursor: pointer;
  text-align: center;
`;

const ErrorText = styled(Text)`
  color: #f44336;
  font-size: 12px;
  margin-top: 8px;
  text-align: left;
  width: 100%;
  display: block;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
`;

const Loader = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #9ebe21;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoaderMessage = styled(Text)`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

function CreateAccount() {
  const navigate = useNavigate();
  mixPanel.init();
  const [searchParams] = useSearchParams();
  const getResponseSummary = useCallback(resp => {
    if (!resp) return null;
    const source = resp.response ?? resp;
    if (!source || typeof source !== 'object') return null;
    const { config, data, status } = source;
    const summary = {};
    if (typeof config !== 'undefined') summary.config = config;
    if (typeof data !== 'undefined') summary.data = data;
    if (typeof status !== 'undefined') summary.status = status;
    return Object.keys(summary).length ? summary : null;
  }, []);
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [mobile, setMobile] = useState('');
  const [countryIso, setCountryIso] = useState('IN');
  const [projectId, setProjectId] = useState('');
  const [token1, setToken1] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [nameError, setNameError] = useState('');
  const [termsError, setTermsError] = useState('');
  const [showInvalidScreen, setShowInvalidScreen] = useState(false);
  const [showProjectClosedScreen, setShowProjectClosedScreen] = useState(false);
  const [projectClosedVariant, setProjectClosedVariant] = useState('ended');
  const [isValidatingWeblink, setIsValidatingWeblink] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasVerifiedPhone, setHasVerifiedPhone] = useState(false);

  const displayMobile = countryCode && mobile ? `${countryCode} ${mobile}` : mobile || '';
  const isCreateReady = termsAccepted && name.trim().length > 0 && Boolean(mobile) && !submitting;

  useEffect(() => {
    if (!errorMessage && !nameError && !termsError) return;
    const timeoutId = setTimeout(() => {
      setErrorMessage('');
      setNameError('');
      setTermsError('');
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [errorMessage, nameError, termsError]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pendingCreateAccount');
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (pending?.mobile) {
        setMobile(String(pending.mobile));
        setHasVerifiedPhone(true);
      }
      if (pending?.dial_code) {
        const dial = String(pending.dial_code).startsWith('+')
          ? String(pending.dial_code)
          : `+${String(pending.dial_code).replace(/^\+/, '')}`;
        setCountryCode(dial);
      }
      if (pending?.country_code) {
        setCountryIso(String(pending.country_code));
      }
    } catch (error) {
      console.error('Error reading pendingCreateAccount:', error);
    }
  }, []);

  useEffect(() => {
    const validateWeblinkAndSetParams = async () => {
      const urlValidation = validateWebLinkUrl();
      if (!urlValidation.isValid) {
        console.error('URL validation failed:', urlValidation.error);
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      const validatedIdentificationID = urlValidation.identificationID;
      const validatedProjectIdentity = urlValidation.projectname || urlValidation.projectId;

      if (!validatedIdentificationID) {
        console.error('No URL parameter found after ? mark');
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      try {
        setIsValidatingWeblink(true);
        const payload = {
          identificationID: validatedIdentificationID,
          projectIdentity: validatedProjectIdentity,
        };

        const response = await maverickPortalhttp.makeRequest(
          maverickPortalApiEndPoint.weblinkValidation.url,
          maverickPortalApiEndPoint.weblinkValidation.methodType,
          payload
        );

        if (response && response.data.Status && response.data.Status.StatusCode === '-1') {
          if (response.data.Response) {
            const projectStatusText = String(response.data.Response.projectStatus ?? '').toLowerCase();
            const projectEndVal = response.data.Response.projectEnd;
            const accessLoginVal = response.data.Response.accessLogin;

            const isProjectEndZero = projectEndVal === 0 || projectEndVal === '0';
            const isProjectFull =
              projectStatusText.includes('full') ||
              projectStatusText.includes('quota') ||
              projectStatusText.includes('maximum') ||
              projectStatusText.includes('max');
            const isAccessLoginBlocked = accessLoginVal === 0 || accessLoginVal === '0';

            const shouldShowProjectClosedScreen = isProjectEndZero || isProjectFull || isAccessLoginBlocked;
            if (shouldShowProjectClosedScreen) {
              setProjectClosedVariant(isProjectFull || isAccessLoginBlocked ? 'full' : 'ended');
              setShowProjectClosedScreen(true);
              setShowInvalidScreen(false);
              setIsValidatingWeblink(false);
              return;
            }

            if (response.data.Response.isAvaliableProjectFlag === 0) {
              setShowInvalidScreen(true);
              setIsValidatingWeblink(false);
              return;
            }
          }
        }

        setIsValidatingWeblink(false);
        setProjectId(searchParams.get('Project_ID'));
        setToken1(searchParams.get('Token'));
      } catch (error) {
        console.error('Error validating weblink:', error);
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
      }
    };

    validateWeblinkAndSetParams();
  }, [searchParams]);

  const goToLogin = () => {
    localStorage.removeItem('pendingCreateAccount');
    const currentUrl = window.location.href;
    const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
    const token5 = currentUrl.split('=')[1] || '';
    if (!projectId) {
      navigate(`/login-Resp?${projectName}=${token5}`);
    } else {
      navigate(`/login-Resp?Project_ID=${projectId}&Token=${token1}`);
    }
  };

  const handleSignUp = () => {
    setErrorMessage('');
    setNameError('');
    setTermsError('');

    if (!name.trim()) {
      setNameError('Enter Your Name');
      return;
    }

    if (!mobile.trim()) {
      setErrorMessage('Verified mobile number is missing. Please use a different number.');
      return;
    }

    if (!termsAccepted) {
      setTermsError('Please accept the Terms and Privacy Policy.');
      return;
    }

    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const isGeneralized = !currentUrl.includes('&');
    const personalizedToken = urlParams.get('Token') || token1 || '';
    const genIDFromUrl = isGeneralized
      ? currentUrl.split('=')[1] || ''
      : '';

    const data = {
      name: name.trim(),
      mobile,
      phone: `${countryCode}${mobile}`,
      country_code: countryIso || 'IN',
      isGeneralized: isGeneralized ? 1 : 0,
      genID: isGeneralized ? genIDFromUrl : '',
      ...(isGeneralized ? {} : { perID: personalizedToken }),
    };

    setSubmitting(true);
    maverickPortalhttp
      .makeRequest(
        maverickPortalApiEndPoint.createWebLinkAccount2.url,
        maverickPortalApiEndPoint.createWebLinkAccount2.methodType,
        data
      )
      .then(response => {
        setSubmitting(false);
        if (response.data?.Status?.StatusCode == 1 || response.data?.Status?.StatusCode === '1') {
          localStorage.removeItem('pendingCreateAccount');
          localStorage.removeItem('signupData');

          const resp = response.data?.Response || {};

          // Same storage as checkWeblink / OTP success — use response genID for
          // getWeblinkRespondentDetails and other internal weblink APIs.
          if (resp?.token_data?.access_token) {
            localStorage.setItem('token', resp.token_data.access_token);
          }
          if (resp?.user_id != null) {
            localStorage.setItem('user_id', String(resp.user_id));
          }
          if (resp?.user_name || name.trim()) {
            localStorage.setItem('user_name', resp.user_name || name.trim());
          }
          if (resp?.project_ID != null) {
            localStorage.setItem('project_IDper', String(resp.project_ID));
          }
          if (resp?.projectName) {
            localStorage.setItem('project_name', resp.projectName);
          }
          if (resp?.genID) {
            localStorage.setItem('gen_ID', resp.genID);
          }

          const currentUrl = window.location.href;
          const urlParams = new URLSearchParams(window.location.search);
          const isGeneralizedNav = !currentUrl.includes('&');
          let tokenFromParams = urlParams.get('Token') || token1;
          if (!tokenFromParams) {
            const urlParts = currentUrl.split('?')[1];
            if (urlParts) {
              const paramParts = urlParts.split('=');
              if (paramParts.length > 1) {
                tokenFromParams = paramParts[1];
              }
            }
          }

          const projectNameForNav =
            resp?.projectName || currentUrl.split('?')[1]?.split('=')[0] || '';

          if (!isGeneralizedNav) {
            navigate(
              `/dashboard-resp?${projectNameForNav}&Token=${tokenFromParams}`,
              { replace: true }
            );
          } else {
            navigate(
              `/dashboard-resp?${projectNameForNav}=${tokenFromParams}`,
              { replace: true }
            );
          }
        } else if (response.data?.Status?.StatusCode == -1) {
          const currentUrl = window.location.href;
          const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
          mixPanel.track('Create account Falied', {
            name,
            mobile,
            countryCode,
            errorCode: 442,
            error: response?.data?.Status?.StatusDesc || 'Create account failed',
            endpoint: maverickPortalApiEndPoint.createWebLinkAccount.url,
            timestamp: new Date().toISOString(),
            request: data,
            projectName,
            errorDetails: getResponseSummary(response),
          });
          setErrorMessage(response.data?.Status?.StatusDesc || 'Failed to create account');
        }
      })
      .catch(error => {
        setSubmitting(false);
        console.error('Error creating account:', error);
        const errorReason =
          error.response?.data?.Status?.StatusDesc ||
          error.response?.data?.message ||
          error.message ||
          'Unknown error';
        mixPanel.track('Create account Falied', {
          error: `error from create account: ${errorReason}`,
          name,
          mobile,
          countryCode,
          errorCode: 442,
          errorDetails: getResponseSummary(error),
          endpoint: maverickPortalApiEndPoint.createWebLinkAccount.url,
          timestamp: new Date().toISOString(),
          request: data,
          projectId,
          token: token1,
        });
        setErrorMessage('Failed to create account. Please try again.');
      });
  };

  if (isValidatingWeblink) {
    return (
      <LoaderWrapper>
        <Loader />
        <LoaderMessage>Loading...</LoaderMessage>
      </LoaderWrapper>
    );
  }

  if (showProjectClosedScreen) {
    return (
      <ProjectClosedScreen
        backgroundImageUrl={ErrorScreen1}
        title={projectClosedVariant === 'full' ? 'Project is Full' : undefined}
        description={
          projectClosedVariant === 'full'
            ? 'This project has reached its participant limit and is no longer accepting responses.'
            : undefined
        }
      />
    );
  }

  if (showInvalidScreen) {
    return <InvalidProjectScreen backgroundImageUrl={ErrorScreen1} />;
  }

  return (
    <SignUpPage>
      <SignUpContent>
        <Logo src={`${process.env.PUBLIC_URL}/logo.png`} alt="explorastory" />
        <HeroTitle>Let&apos;s create your account</HeroTitle>
        <HeroSubtitle>
          There&apos;s no Explorastory account for this number yet. Please add a few details to create
          one.
        </HeroSubtitle>

        <FormGroup>
          <FieldLabel>Mobile number</FieldLabel>
          <VerifiedMobileBox>
            <MobileValue>{displayMobile || '—'}</MobileValue>
            {hasVerifiedPhone && (
              <VerifiedBadge>
                <CheckIcon aria-hidden="true" />
                Verified
              </VerifiedBadge>
            )}
          </VerifiedMobileBox>
        </FormGroup>

        <FormGroup style={{ marginTop: '20px' }}>
          <FieldLabel htmlFor="create-account-name">Enter your name and get started</FieldLabel>
          <NameInput
            id="create-account-name"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            placeholder="Your name"
            autoComplete="name"
          />
          {nameError && <ErrorText>{nameError}</ErrorText>}
        </FormGroup>

        <TermsRow>
          <TermsCheckbox
            type="checkbox"
            checked={termsAccepted}
            onChange={e => {
              setTermsAccepted(e.target.checked);
              if (termsError) setTermsError('');
            }}
          />
          <span>
            I agree to the{' '}
            <TermsLink
              href="https://www.humanifytechnologies.com/respondent-terms-and-condition"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Terms
            </TermsLink>{' '}
            and{' '}
            <TermsLink
              href="https://www.humanifytechnologies.com/respondent-privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Privacy Policy
            </TermsLink>
            , and consent to my video responses being recorded for research.
          </span>
        </TermsRow>
        {(termsError || errorMessage) && <ErrorText>{termsError || errorMessage}</ErrorText>}

        <CreateAccountButton type="button" onClick={handleSignUp} disabled={!isCreateReady}>
          {submitting ? 'Creating...' : 'Create Account'}
        </CreateAccountButton>

        <Spacer />

        <DifferentNumberLink type="button" onClick={goToLogin}>
          Not your number? Use a different one
        </DifferentNumberLink>
      </SignUpContent>
    </SignUpPage>
  );
}

export default CreateAccount;
